import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateTask } from "../api";
import { TOAST_IDS, ToastType } from "@/modules/common/types";
import { showToast } from "@/modules/common/utils";

interface UpdateTaskOptions {
  onSuccess?: () => void;
  onError?: () => void;
  showDefaultToasts?: boolean;
  successMessage?: string;
  errorMessage?: string;
  invalidateQueries?: boolean;
  queryKeys?: string[];
}

const useUpdateTask = (options: UpdateTaskOptions = {}) => {
  const queryClient = useQueryClient();

  const {
    onSuccess: customOnSuccess,
    onError: customOnError,
    showDefaultToasts = true,
    successMessage = "Task completed!",
    errorMessage = "Failed to complete task",
    invalidateQueries = true,
    queryKeys = ["tasks-dashboard", "task-statistics", "tasks-count"],
  } = options;

  return useMutation({
    mutationFn: (taskData: { id: string; status: string }) =>
      updateTask({
        id: taskData.id,
        updateTaskRequest: {
          metaData: { id: taskData.id, status: taskData.status },
        },
      }),
    onSuccess: () => {
      if (invalidateQueries) {
        queryKeys.forEach((queryKey) => {
          queryClient.invalidateQueries({ queryKey: [queryKey] });
        });
      }
      if (showDefaultToasts) {
        showToast({
          type: ToastType.SUCCESS,
          message: successMessage,
          toastId: TOAST_IDS.TASK_COMPLETED_SUCCESS,
          options: {
            position: "bottom-left",
            autoClose: 3000,
          },
        });
      }
      customOnSuccess?.();
    },
    onError: () => {
      if (showDefaultToasts) {
        showToast({
          type: ToastType.ERROR,
          message: errorMessage,
          toastId: TOAST_IDS.TASK_COMPLETED_ERROR,
          options: {
            position: "bottom-left",
            autoClose: 3000,
          },
        });
      }
      customOnError?.();
    },
  });
};

export default useUpdateTask;
